'use client';

import { cn } from '@/lib/utils';
import { batteryLevel, batteryPercent } from '@/lib/drone-telemetry';
import type { LiveDrone } from '@/hooks/useWebSocket';

const CHIP_HALF_WIDTH = 90;
const CURSOR_GAP = 14;

const BATTERY_TEXT: Record<'ok' | 'warn' | 'critical', string> = {
  ok: 'text-status-green-fg',
  warn: 'text-status-amber-fg',
  critical: 'text-status-red-fg',
};

/**
 * Level one of the drone interaction: enough to answer "is this the one I
 * want?" without committing to a selection. Callsign, battery, altitude — no
 * more. The selected drone's card supersedes it, so the two never overlap.
 */
export function DroneHoverChip({
  drone,
  x,
  y,
  containerWidth,
}: {
  drone: LiveDrone;
  x: number;
  y: number;
  /** Used to keep the chip inside the map at the edges. */
  containerWidth?: number;
}) {
  const battery = batteryPercent(drone);
  const callsign = drone.callsign?.trim() || drone.drone_id.slice(-6).toUpperCase();

  const left =
    containerWidth && containerWidth > CHIP_HALF_WIDTH * 2
      ? Math.min(Math.max(x, CHIP_HALF_WIDTH), containerWidth - CHIP_HALF_WIDTH)
      : x;

  return (
    <div
      // pointer-events-none is load-bearing: the chip sits directly over the
      // marker the operator is about to click.
      //
      // `bg-popover` opaque, for the same arithmetic as DroneSelectionChip, its
      // sibling: this paints `--muted-foreground` at 11px, and the 5% of basemap
      // that `/95` let through dropped it to 4.32:1 over the pale CARTO voyager
      // — the app theme and the basemap style toggle independently, so there is
      // no ground to design against. Opaque is 4.81:1 on any basemap.
      className="absolute z-30 pointer-events-none flex items-center gap-2 whitespace-nowrap rounded-md border border-subtle bg-popover px-2 py-1 text-[11px] shadow-lg"
      style={{ left, top: y, transform: `translate(-50%, calc(-100% - ${CURSOR_GAP}px))` }}
      role="status"
      aria-live="off"
    >
      <span className="callsign text-[11px] text-foreground">{callsign}</span>
      <span className="h-3 w-px bg-border" aria-hidden />
      {battery === null ? (
        // RemoteID broadcasts no battery state — "N/A", never a misleading 0%.
        <span className="text-muted-foreground">N/A</span>
      ) : (
        <span className={cn('font-mono tabular-nums', BATTERY_TEXT[batteryLevel(battery)])}>{battery}%</span>
      )}
      <span className="h-3 w-px bg-border" aria-hidden />
      <span className="font-mono tabular-nums text-muted-foreground">{Math.round(drone.altitude ?? 0)}m</span>
    </div>
  );
}
